<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  STATUS_LABELS,
  STATUS_TAG,
  arriveTrip,
  canAct,
  cancelTrip,
  createTrip,
  departTrip,
  getTrip,
  listTrips,
  type TripDetail,
  type TripInput,
  type TripRow,
} from '../api/trips'
import { listDriverOptions, type Driver } from '../api/drivers'
import {
  PRIORITY_LABELS,
  SOURCE_LABELS,
  STATUS_LABELS as TASK_STATUS_LABELS,
  type Task,
} from '../api/dispatch'
import { listRouteOptions, type Route } from '../api/routes'
import { listVehicleOptions, type VehicleRow } from '../api/vehicles'

const rows = ref<TripRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const refError = ref<unknown>(null)
const notice = ref('')

const vehicles = ref<VehicleRow[]>([])
const routes = ref<Route[]>([])
const drivers = ref<Driver[]>([])

// 参照下拉用 undefined 表示「未选」：0 会被 el-select 当成值显示成「0」，placeholder 就没了
const query = reactive({
  keyword: '',
  status: '',
  vehicle_id: undefined as number | undefined,
  route_id: undefined as number | undefined,
  page: 1,
  size: 20,
})

const formOpen = ref(false)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive({
  vehicle_id: undefined as number | undefined,
  route_id: undefined as number | undefined,
  driver_id: undefined as number | undefined,
  load_desc: '',
  remark: '',
})

const detailOpen = ref(false)
const detail = ref<TripDetail | null>(null)
const detailLoading = ref(false)
const detailError = ref<unknown>(null)

const formErrorText = computed(() => (form.vehicle_id ? '' : '请选择车辆'))

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listTrips({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
      vehicle_id: query.vehicle_id,
      route_id: query.route_id,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function loadRefs(): Promise<void> {
  refError.value = null
  try {
    const [v, r, d] = await Promise.all([listVehicleOptions(), listRouteOptions(), listDriverOptions()])
    vehicles.value = v
    routes.value = r
    drivers.value = d
  } catch (e) {
    // 参照数据拿不到下拉会空，但必须把原因露出来，否则页面看着像没数据
    refError.value = e
  }
}

onMounted(() => {
  void load()
  void loadRefs()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function openCreate(): void {
  Object.assign(form, {
    vehicle_id: undefined,
    route_id: undefined,
    driver_id: undefined,
    load_desc: '',
    remark: '',
  })
  formError.value = null
  formOpen.value = true
}

/** 换车时带上这台车绑定的司机，避免每次都要手选 */
function onVehiclePicked(): void {
  const v = vehicles.value.find((item) => item.id === form.vehicle_id)
  if (!form.driver_id && v?.driver_id) form.driver_id = v.driver_id
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input: TripInput = {
    vehicle_id: form.vehicle_id ?? 0,
    route_id: form.route_id,
    driver_id: form.driver_id,
    load_desc: form.load_desc.trim() || undefined,
    remark: form.remark.trim() || undefined,
  }
  try {
    const created = await createTrip(input)
    formOpen.value = false
    await load(`行车任务 ${created.doc_no} 已创建`)
  } catch (e) {
    // 同一辆车已有未完成任务时后端回 409，这里原样把 trace_id 露出来
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function openDetail(row: TripRow): Promise<void> {
  detail.value = null
  detailError.value = null
  detailLoading.value = true
  detailOpen.value = true
  try {
    detail.value = await getTrip(row.id)
  } catch (e) {
    detailError.value = e
  } finally {
    detailLoading.value = false
  }
}

async function act(kind: 'depart' | 'arrive' | 'cancel', row: TripRow): Promise<void> {
  notice.value = ''
  refError.value = null
  try {
    if (kind === 'depart') await departTrip(row.id)
    else if (kind === 'arrive') await arriveTrip(row.id)
    else await cancelTrip(row.id)
    const text = kind === 'depart' ? '已发车' : kind === 'arrive' ? '已到达' : '已取消'
    await load(`${row.doc_no} ${text}`)
  } catch (e) {
    refError.value = e
  }
}
</script>

<template>
  <div class="yc-trip">
    <header class="yc-trip__bar">
      <el-input
        v-model="query.keyword"
        class="yc-trip__keyword"
        placeholder="行车任务号"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.status" class="yc-trip__ref" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-select
        v-model="query.vehicle_id"
        class="yc-trip__ref"
        placeholder="全部车辆"
        clearable
        filterable
        @change="search"
      >
        <el-option v-for="v in vehicles" :key="v.id" :label="v.plate_no" :value="v.id" />
      </el-select>
      <el-select
        v-model="query.route_id"
        class="yc-trip__ref"
        placeholder="全部线路"
        clearable
        filterable
        @change="search"
      >
        <el-option v-for="r in routes" :key="r.id" :label="`${r.code} ${r.name}`.trim()" :value="r.id" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'dsp:trip:create'" type="primary" @click="openCreate">新建行车任务</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-trip__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-trip__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-trip__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="doc_no" label="行车任务号" min-width="150" />
        <el-table-column label="线路" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">{{ row.route_name || '—' }}</template>
        </el-table-column>
        <el-table-column label="车辆" min-width="120" show-overflow-tooltip>
          <template #default="{ row }">{{ row.plate_no || `#${row.vehicle_id}` }}</template>
        </el-table-column>
        <el-table-column label="司机" min-width="110" show-overflow-tooltip>
          <template #default="{ row }">{{ row.driver_name || '—' }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as TripRow['status']] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as TripRow['status']] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="所载任务" width="100" align="right">
          <template #default="{ row }">{{ row.task_count }}</template>
        </el-table-column>
        <el-table-column label="装载（kg / cm³）" width="180" align="right">
          <template #default="{ row }">
            {{ Number(row.load_weight_kg).toFixed(3) }} / {{ Number(row.load_volume_cm3).toFixed(2) }}
          </template>
        </el-table-column>
        <el-table-column label="发车时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.depart_at) }}</template>
        </el-table-column>
        <el-table-column label="到达时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.arrive_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="210" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDetail(row)">详情</el-button>
            <el-button
              v-if="canAct(row.status, 'depart')"
              v-perm="'dsp:trip:depart'"
              link
              type="primary"
              @click="act('depart', row)"
            >
              发车
            </el-button>
            <el-button
              v-if="canAct(row.status, 'arrive')"
              v-perm="'dsp:trip:arrive'"
              link
              type="primary"
              @click="act('arrive', row)"
            >
              到达
            </el-button>
            <el-popconfirm
              v-if="canAct(row.status, 'cancel')"
              title="确认取消该行车任务？未发车的任务会释放回待调度。"
              width="300"
              @confirm="act('cancel', row)"
            >
              <template #reference>
                <el-button v-perm="'dsp:trip:cancel'" link type="danger">取消</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState v-else-if="!loading" title="还没有行车任务" description="派车后会自动生成行车任务，也可以在这里手工新建。">
        <el-button v-perm="'dsp:trip:create'" type="primary" @click="openCreate">新建行车任务</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-trip__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      title="新建行车任务"
      width="540px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-trip__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="车辆" :error="formErrorText">
          <el-select
            v-model="form.vehicle_id"
            class="yc-trip__full"
            filterable
            placeholder="选择出车的车辆"
            @change="onVehiclePicked"
          >
            <el-option v-for="v in vehicles" :key="v.id" :label="v.plate_no" :value="v.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="线路">
          <el-select v-model="form.route_id" class="yc-trip__full" clearable filterable placeholder="可留空">
            <el-option v-for="r in routes" :key="r.id" :label="`${r.code} ${r.name}`.trim()" :value="r.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="司机">
          <el-select v-model="form.driver_id" class="yc-trip__full" clearable filterable placeholder="可留空">
            <el-option v-for="d in drivers" :key="d.id" :label="`${d.code} ${d.name}`.trim()" :value="d.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="装载说明">
          <el-input v-model="form.load_desc" maxlength="255" placeholder="如 日用百货 6 托，可留空" />
        </el-form-item>

        <el-form-item label="备注">
          <el-input v-model="form.remark" maxlength="255" placeholder="可留空" />
        </el-form-item>

        <p class="yc-trip__hint">同一辆车同时只能有一张未完成的行车任务，重复新建会被后端拒绝（409）。</p>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">创建</el-button>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>

    <el-dialog v-if="detailOpen" v-model="detailOpen" :title="`行车任务详情${detail ? ' · ' + detail.doc_no : ''}`" width="840px">
      <div v-if="detailError" class="yc-trip__error">
        <TraceIdAlert :error="detailError" />
      </div>

      <div v-loading="detailLoading">
        <template v-if="detail">
          <div class="yc-trip__facts">
            <div class="yc-trip__fact"><span>状态</span>{{ STATUS_LABELS[detail.status] ?? detail.status }}</div>
            <div class="yc-trip__fact"><span>车辆</span>{{ detail.plate_no || `#${detail.vehicle_id}` }}</div>
            <div class="yc-trip__fact"><span>司机</span>{{ detail.driver_name || '—' }}</div>
            <div class="yc-trip__fact"><span>线路</span>{{ detail.route_name || '—' }}</div>
            <div class="yc-trip__fact"><span>装载说明</span>{{ detail.load_desc || '—' }}</div>
            <div class="yc-trip__fact"><span>备注</span>{{ detail.remark || '—' }}</div>
            <div class="yc-trip__fact"><span>发车时间</span>{{ formatDateTime(detail.depart_at) }}</div>
            <div class="yc-trip__fact"><span>到达时间</span>{{ formatDateTime(detail.arrive_at) }}</div>
          </div>

          <h3 class="yc-trip__sect">所载调度任务（{{ detail.tasks.length }}）</h3>
          <el-table v-if="detail.tasks.length" :data="detail.tasks" row-key="id">
            <el-table-column prop="task_no" label="任务号" min-width="150" />
            <el-table-column label="来源" min-width="150" show-overflow-tooltip>
              <template #default="{ row }">
                <el-tag size="small" type="info">{{ SOURCE_LABELS[row.source_type as Task['source_type']] ?? row.source_type }}</el-tag>
                {{ row.source_no || '—' }}
              </template>
            </el-table-column>
            <el-table-column prop="cargo_desc" label="货描" min-width="150" show-overflow-tooltip />
            <el-table-column label="优先级" width="90">
              <template #default="{ row }">{{ PRIORITY_LABELS[row.priority as Task['priority']] ?? row.priority }}</template>
            </el-table-column>
            <el-table-column label="重量（kg）" width="120" align="right">
              <template #default="{ row }">{{ Number(row.weight_kg).toFixed(3) }}</template>
            </el-table-column>
            <el-table-column label="体积（cm³）" width="130" align="right">
              <template #default="{ row }">{{ Number(row.volume_cm3).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column label="状态" width="100">
              <template #default="{ row }">
                <el-tag size="small">{{ TASK_STATUS_LABELS[row.status as Task['status']] ?? row.status }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
          <p v-else class="yc-trip__hint">这张行车任务还没有挂调度任务。</p>
        </template>
      </div>

      <template #footer>
        <el-button @click="detailOpen = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-trip {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 170px;
  }

  &__ref {
    width: 150px;
  }

  &__full {
    width: 100%;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__hint {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.7;
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: var(--yc-space-2) var(--yc-space-4);
    margin-bottom: var(--yc-space-4);
  }

  &__fact {
    font-size: var(--yc-font);

    span {
      margin-right: var(--yc-space-2);
      color: var(--yc-text-secondary);
      font-size: var(--yc-font-sm);
    }
  }

  &__sect {
    margin: var(--yc-space-4) 0 var(--yc-space-2);
    font-size: var(--yc-font);
    font-weight: 600;
  }
}
</style>
